import { useMutation } from "@tanstack/react-query";
import { KeyRound, UserX } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";

import { PasswordChangeForm } from "@/components/password-change-form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { UserAvatar } from "@/components/user-avatar";
import { t } from "@/i18n/pl";
import { ApiError } from "@/services/api-client";
import * as authService from "@/services/auth.service";

function toastError(error: unknown): void {
  toast.error(error instanceof ApiError ? error.message : t.common.genericError);
}

/** Own account: password, or closing the account. */
export function SettingsPage() {
  const { user, signOut } = useAuth();
  const [closeOpen, setCloseOpen] = useState(false);
  const [password, setPassword] = useState("");

  const closeAccount = useMutation({
    mutationFn: () => authService.closeAccount(password),
    onSuccess: async () => {
      toast.success(t.settings.accountClosed);
      // The server has already revoked every session; this drops the
      // in-memory token, the cookie and the query cache, and lands on /login.
      await signOut();
    },
    onError: toastError,
  });

  if (!user) return null;

  function handleClose(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    closeAccount.mutate();
  }

  return (
    <div className="animate-in fade-in flex flex-col gap-5 duration-300">
      <header className="flex items-center gap-3">
        <UserAvatar
          user={user}
          className="size-14"
          fallbackClassName="bg-secondary text-lg font-semibold"
        />
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold tracking-tight">{user.full_name}</h1>
          <p className="text-muted-foreground truncate text-sm">{user.username}</p>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="text-primary size-5" aria-hidden />
            {t.settings.changePasswordTitle}
          </CardTitle>
          <CardDescription>{t.settings.changePasswordBody}</CardDescription>
        </CardHeader>
        <CardContent>
          <PasswordChangeForm
            username={user.username}
            currentPasswordLabel={t.settings.currentPassword}
            submitLabel={t.settings.changePassword}
            successMessage={t.settings.passwordChanged}
          />
        </CardContent>
      </Card>

      {/* Administrators are offboarded by another administrator, never by
          their own click — the API refuses it, so the block is not shown. */}
      {!user.is_admin && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="text-destructive size-5" aria-hidden />
              {t.settings.closeAccountTitle}
            </CardTitle>
            <CardDescription>{t.settings.closeAccountBody}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={() => setCloseOpen(true)}>
              {t.settings.closeAccount}
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={closeOpen}
        onOpenChange={(open) => {
          setCloseOpen(open);
          if (!open) setPassword("");
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.settings.closeAccountConfirmTitle}</DialogTitle>
            <DialogDescription>{t.settings.closeAccountConfirmBody}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleClose}>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="close-password">{t.settings.currentPassword}</FieldLabel>
                <Input
                  id="close-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={closeAccount.isPending}
                />
              </Field>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCloseOpen(false)}>
                  {t.common.cancel}
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={closeAccount.isPending || password.length === 0}
                >
                  {closeAccount.isPending ? t.settings.closing : t.settings.closeAccount}
                </Button>
              </DialogFooter>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
